import { useState } from "react";
import { Color, ColorChangeHandler } from "react-color";

import { FrameStencilsApi, PlayStateApi, SelectItemApi, UpdateFrameStencilsFunc } from "../../../hooks";
import { AnimationTimerApi } from "../../animation-timeline";
import { useFastBuild } from "./use-fast-build";

export interface DrawToolbarStateItem {
  isActive: boolean;
  toggle: () => void;
  cancel: () => void;
}

export interface DrawColor extends DrawToolbarStateItem {
  color: Color;
  handleChangeColor: ColorChangeHandler;
}

const useDrawToolbarStateItem = (initialState = false): DrawToolbarStateItem => {
  const [isActive, setIsActive] = useState<boolean>(initialState);
  const toggle = () => setIsActive((prevState) => !prevState);
  const cancel = () => setIsActive(false);

  return { isActive, toggle, cancel };
};

export interface UseDrawToolbarApiProps {
  selectItemApi: SelectItemApi;
  playStateApi: PlayStateApi;
  animationTimerApi: AnimationTimerApi;
  frameStencilsApi: FrameStencilsApi;
}

export interface GiveBallProps {
  selectedItems: SelectItemApi["selectedItems"];
  updateFrameStencils: UpdateFrameStencilsFunc;
}

export const useDrawToolbarApi = ({
  selectItemApi,
  playStateApi,
  animationTimerApi,
  frameStencilsApi,
}: UseDrawToolbarApiProps) => {
  const { selectedItems } = selectItemApi;
  const { playState, setPlayState, activeFrame, setActiveFrameId, undoPlayState, redoPlayState } = playStateApi;
  const { resetTimer } = animationTimerApi;
  const { updatePossession } = frameStencilsApi;

  const freeDraw = useDrawToolbarStateItem();
  const fastPlace = useDrawToolbarStateItem();
  const animationView = useDrawToolbarStateItem();

  const animationViewToggle = () => {
    animationView.toggle();
    resetTimer();
  };

  const { fastBuild } = useFastBuild({ activeFrame, playFrames: playState.frames, setActiveFrameId, setPlayState });
  const giveBall = () => updatePossession({ selectedItems });

  const color: Color = "#000";
  const handleChangeColor: ColorChangeHandler = () => undefined;
  const drawColor = { ...useDrawToolbarStateItem(), color, handleChangeColor };

  return {
    freeDraw,
    fastPlace,
    animationView: { ...animationView, toggle: animationViewToggle },
    fastBuild,
    giveBall,
    drawColor,
    undo: undoPlayState,
    redo: redoPlayState,
  };
};

export type DrawToolbarApi = ReturnType<typeof useDrawToolbarApi>;

export const DEFAULT_DRAW_TOOLBAR_API: DrawToolbarApi = {
  freeDraw: { isActive: false, toggle: () => undefined, cancel: () => undefined },
  fastPlace: { isActive: false, toggle: () => undefined, cancel: () => undefined },
  animationView: { isActive: false, toggle: () => undefined, cancel: () => undefined },
  fastBuild: () => Promise.resolve(),
  giveBall: () => undefined,
  drawColor: {
    isActive: false,
    toggle: () => undefined,
    cancel: () => undefined,
    color: "#000",
    handleChangeColor: () => undefined,
  },
  undo: () => undefined,
  redo: () => undefined,
};
